import type {
  ChatMessageContent,
  ChatMessage as ChatMessageType,
} from "schemas/generated/chatMessage";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import ChangeBlock from "@/features/react-editor/components/chat/chat-elements/ChangeBlock";
import PageGenerationRenderer from "@/features/react-editor/components/chat/chat-elements/PageGenerationRenderer";
import SuggestedTemplates from "@/features/react-editor/components/chat/chat-elements/SuggestedTemplates";
import { useMessages } from "@/features/react-editor/components/chat/store/ChatStoreContext";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Expandable } from "@replo/design-system/components/expandable/Expandable";
import {
  Card,
  CardContent,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";
import { CircleX, ImageIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { parseJsonOrNull } from "replo-utils/lib/json";
import {
  chatBuildErrorItemSchema,
  chatProductItemSchema,
  chatRevertItemSchema,
} from "schemas/chatMessage";

import { ChatTextRenderer } from "./ChatTextRenderer";
import { MarkdownRenderer } from "./MarkdownRenderer";

interface Message {
  id: string;
  role: "user" | "assistant" | (string & {});
  content: ChatMessageContent;
  createdAt?: Date;
  messageId: string;
}

interface ChatMessageProps extends Message {
  showTimeStamp?: boolean;
  animation?: Animation;
  className?: string;
  sendChatRequest?: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  toolMessageId?: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  className,
  sendChatRequest,
  messageId,
  toolMessageId,
}) => {
  const isUser = role === "user";

  if (role === "tool") {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2",
        isUser ? "items-end" : "items-start",
      )}
    >
      <MessageContent
        content={content}
        role={role}
        className={className}
        sendChatRequest={sendChatRequest}
        messageId={messageId}
        toolMessageId={toolMessageId}
      />
    </div>
  );
};

const MessageContent: React.FC<{
  content: ChatMessageContent;
  role: string;
  className?: string;
  sendChatRequest?: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  messageId: string;
  toolMessageId?: string;
}> = ({
  content,
  role,
  className,
  sendChatRequest,
  messageId,
  toolMessageId,
}) => {
  return (
    <div
      className={twMerge(
        "flex gap-1 items-end w-full min-w-0",
        role === "user" ? "flex-row" : "flex-col",
      )}
    >
      {content.map((item, index) => (
        <div className="flex gap-3 w-full" key={`${item.type}-${index}`}>
          <MessageItem
            item={item}
            role={role}
            className={className}
            sendChatRequest={sendChatRequest}
            messageId={messageId}
            toolMessageId={toolMessageId}
          />
        </div>
      ))}
    </div>
  );
};

const MessageItem: React.FC<{
  item: ChatMessageContent[number];
  role: string;
  className?: string;
  sendChatRequest?: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  messageId: string;
  toolMessageId?: string;
}> = ({ item, role, className, sendChatRequest, messageId, toolMessageId }) => {
  if (item.type === "text") {
    return (
      <TextMessageItem
        item={item}
        role={role}
        className={className}
        sendChatRequest={sendChatRequest}
        messageId={messageId}
      />
    );
  }

  if (item.type === "tool-call") {
    return (
      <ToolCallMessageItem
        item={item}
        className={className}
        toolMessageId={toolMessageId}
      />
    );
  }

  if (item.type === "image") {
    return <ImageMessageItem item={item} />;
  }

  return null;
};

// Text message item renderer
const TextMessageItem: React.FC<{
  item: Extract<ChatMessageContent[number], { type: "text" }>;
  role: string;
  sendChatRequest?: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  className?: string;
  messageId?: string;
}> = ({ item, role, className, sendChatRequest, messageId }) => {
  if (item.isHidden) {
    return null;
  }

  if (item.contentType === "product") {
    return <ProductRenderer text={item.text} />;
  }

  if (item.contentType === "buildError" && sendChatRequest) {
    return (
      <BuildErrorRenderer
        text={item.text}
        sendChatRequest={sendChatRequest}
        messageId={messageId}
      />
    );
  }

  if (item.contentType === "revert") {
    return <RevertRenderer text={item.text} />;
  }

  // Default text content (contentType === "text" or no contentType)
  if (item.contentType === "text" || !item.contentType) {
    const isAssistant = role === "assistant";
    const {
      thinkBlocks,
      cleanedText,
      hasThinkBlocks,
      isStreamingThink,
      streamingThinkContent,
    } = extractThinkBlocks(item.text);

    // Show think section for assistant messages with either completed or streaming think blocks
    const shouldShowThinkingBlock =
      isAssistant && (hasThinkBlocks || isStreamingThink);
    const isThinkingOpen = isStreamingThink; // Open when streaming, closed when completed

    return (
      <div className="flex flex-col">
        {shouldShowThinkingBlock && (
          <div className="p-3 pb-0">
            <Expandable
              key={isStreamingThink ? "streaming" : "completed"} // Force re-render when state changes
              defaultOpen={isThinkingOpen}
              trigger={
                <Button variant="tertiary" size="base">
                  Thinking
                </Button>
              }
              content={
                <div className="pt-2">
                  <div className="text-base text-muted">
                    {isStreamingThink ? (
                      <MarkdownRenderer>
                        {streamingThinkContent}
                      </MarkdownRenderer>
                    ) : (
                      thinkBlocks.map((block, index) => (
                        <div key={index} className="mb-2 last:mb-0">
                          <MarkdownRenderer>{block}</MarkdownRenderer>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              }
            />
          </div>
        )}

        <div
          className={twMerge(
            "text-base relative break-words p-3 rounded-lg text-default",
            role === "user" && "bg-sheet",
            className,
          )}
        >
          {role === "user" ? (
            <ChatTextRenderer
              content={shouldShowThinkingBlock ? cleanedText : item.text}
            />
          ) : (
            <MarkdownRenderer>
              {shouldShowThinkingBlock ? cleanedText : item.text}
            </MarkdownRenderer>
          )}
        </div>
      </div>
    );
  }

  return null;
};

// Tool call message item renderer
const ToolCallMessageItem: React.FC<{
  item: Extract<ChatMessageContent[number], { type: "tool-call" }>;
  className?: string;
  toolMessageId?: string;
}> = ({ item, className, toolMessageId }) => {
  const allMessages = useMessages();
  return (
    <div className={twMerge("text-base px-3 w-full min-w-0", className)}>
      <ToolCallRenderer
        toolName={item.toolName}
        toolResult={findToolResult(item.toolCallId, allMessages)}
        toolMessageId={toolMessageId}
      />
    </div>
  );
};

// Image message item renderer
const ImageMessageItem: React.FC<{
  item: Extract<ChatMessageContent[number], { type: "image" }>;
}> = ({ item }) => {
  if (item.isHidden) {
    return null;
  }

  return (
    <div key={item.id} className="w-[75px] h-[75px] overflow-hidden rounded-lg">
      <img
        src={item.image}
        alt={item.id}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const ProductRenderer = ({ text }: { text: string }) => {
  const product = chatProductItemSchema.parse(JSON.parse(text));

  let featuredMediaUrl = product.featuredMedia?.url ?? "";
  if (product.media?.[0]?.type === "image") {
    featuredMediaUrl = product.media[0].url;
  }

  return (
    <div className="flex gap-3 border border-border rounded-lg p-1 w-[200px]">
      <div className="flex-shrink-0">
        {featuredMediaUrl ? (
          <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden">
            <img
              src={featuredMediaUrl}
              alt={product.title}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md">
            <ImageIcon className="text-muted size-4" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-default truncate">
          {product.title}
        </div>
        <div className="text-xs text-muted mt-1 line-clamp-2">
          {product.description}
        </div>
      </div>
    </div>
  );
};

const BuildErrorRenderer = ({
  text,
  sendChatRequest,
  messageId,
}: {
  text: string;
  sendChatRequest: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  messageId?: string;
}) => {
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");

  const { mutate: updateMessage } = trpc.chat.updateMessage.useMutation({
    onSuccess: () => {
      void trpcUtils.chat.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void trpcUtils.chat.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const validatedText = chatBuildErrorItemSchema.safeParse(JSON.parse(text));
  if (!validatedText.success) {
    console.error("Invalid build error item", validatedText.error);
    return null;
  }

  const buildError = validatedText.data;

  const handleRetry = async () => {
    if (!chatId || !projectId || !messageId) {
      return;
    }

    const prompt = `Please retry that errored change. 
    Errored Version Id: ${buildError.erroredVersionId}
    `;
    await sendChatRequest({ prompt, isHidden: true });
    updateMessage({
      id: messageId,
      projectId,
      content: [
        {
          type: "text",
          contentType: "buildError",
          text: JSON.stringify({
            ...buildError,
            hasRetried: true,
          }),
        },
      ],
    });
  };

  return (
    <div className="text-base px-3 w-full">
      <Card className={twMerge("w-full p-0")}>
        <CardContent className="flex flex-col items-start gap-2 p-4">
          <div className="flex flex-row gap-3 items-start">
            <IconButton
              variant="secondary"
              UNSAFE_className="bg-danger-soft border-danger/50"
              icon={<CircleX size={16} className="text-danger" />}
            />
            <div className="flex flex-col gap-1">
              <div className="text-base font-semibold">
                We couldn't make that change
              </div>
              <div className="text-sm text-muted relative">
                Something went wrong while applying your edit, so we've safely
                reverted to the previous version.
              </div>
            </div>
          </div>
          <div className="flex w-full justify-end">
            <Button
              isLoading={buildError.isReverting}
              disabled={buildError.isReverting || buildError.hasRetried}
              variant="secondary"
              size="sm"
              onClick={() => {
                void handleRetry();
              }}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RevertRenderer = ({ text }: { text: string }) => {
  const parsedText = parseJsonOrNull(text);

  const validatedText = chatRevertItemSchema.safeParse(parsedText);
  if (!validatedText.success) {
    console.error("Invalid revert item", validatedText.error);
    return null;
  }

  const { fileVersionId } = validatedText.data;

  return (
    <div className="flex flex-col gap-2 pt-3.5 pl-3 w-full">
      <div className="text-base">Reverted to previous version</div>
      <ChangeBlock
        isLoading={false}
        appliedDiffs={[]}
        fileVersionId={fileVersionId}
      />
    </div>
  );
};

type ToolResultType = Extract<
  ChatMessageContent[number],
  { type: "tool-result" }
>;

const ToolCallRenderer = ({
  toolName,
  toolResult,
  toolMessageId,
}: {
  toolName: string;
  toolResult: ToolResultType | null;
  toolMessageId?: string;
}) => {
  switch (toolName) {
    case "semanticSearchProductsTool":
      return <div>Let me search for your products</div>;
    case "codeChangeTool":
    case "codeChangeSwarmTool":
    case "retryCodeChangeTool":
      return (
        <ChangeBlock
          isLoading={!toolResult}
          appliedDiffs={toolResult?.result.appliedDiffs}
          fileVersionId={toolResult?.result.fileVersionId}
        />
      );
    case "suggestTemplatesTool":
      return (
        <SuggestedTemplates
          toolResult={toolResult}
          toolMessageId={toolMessageId}
        />
      );
    case "generatePageTool":
      return (
        <PageGenerationRenderer
          toolResult={toolResult}
          toolMessageId={toolMessageId}
        />
      );
    default:
      return null;
  }
};

const findToolResult = (
  toolCallId: string,
  allMessages: ChatMessageType[],
): ToolResultType | null => {
  const toolResultMessage = allMessages.find((message) => {
    if (message.role !== "tool") {
      return false;
    }

    return message.content.some(
      (item) => item.type === "tool-result" && item.toolCallId === toolCallId,
    );
  });

  return toolResultMessage?.content.find(
    (item) => item.type === "tool-result" && item.toolCallId === toolCallId,
  ) as ToolResultType | null;
};

const extractThinkBlocks = (text: string) => {
  const thinkRegex = /<think>([\S\s]*?)<\/think>/g;
  const thinkBlocks: string[] = [];
  let match;

  // Check for streaming think block (opening tag without closing tag)
  const streamingThinkMatch = text.match(/^<think>([\S\s]*?)(?!<\/think>)$/);
  const isStreamingThink = streamingThinkMatch && !text.includes("</think>");

  if (isStreamingThink) {
    const streamingContent = streamingThinkMatch[1] ?? "";
    return {
      thinkBlocks: [],
      cleanedText: "",
      hasThinkBlocks: false,
      isStreamingThink: true,
      streamingThinkContent: streamingContent,
    };
  }

  // Extract completed think blocks
  match = thinkRegex.exec(text);
  while (match !== null) {
    thinkBlocks.push(match[1]?.trim() ?? "");
    match = thinkRegex.exec(text);
  }

  // Remove think blocks from the original text
  const cleanedText = text.replace(thinkRegex, "").trim();

  return {
    thinkBlocks,
    cleanedText,
    hasThinkBlocks: thinkBlocks.length > 0,
    isStreamingThink: false,
    streamingThinkContent: "",
  };
};
